import type { Root, Element, Text } from 'hast';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

/**
 * Embed types we rewrite out of fenced code blocks so a client renderer can
 * draw them instead of Shiki highlighting their source. The map keys are the
 * fenced-code language (`language-<key>`); the value is the `data-embed`
 * attribute the React preview maps to a lazy renderer.
 */
/** The embed kinds Slate ships with: fenced-code language -> `data-embed` value. */
export const BUILTIN_EMBEDS: Record<string, string> = {
  mermaid: 'mermaid',
  vega: 'vega',
  'vega-lite': 'vega-lite',
};

/**
 * Extract the `language-*` token from a hast element's className list.
 * markdown fenced code lands on the inner `<code>` as `language-<lang>`.
 */
function languageOf(node: Element): string | undefined {
  const className = node.properties?.className;
  const classes = Array.isArray(className)
    ? className.map(String)
    : typeof className === 'string'
      ? className.split(/\s+/)
      : [];
  for (const cls of classes) {
    if (cls.startsWith('language-')) return cls.slice('language-'.length);
  }
  return undefined;
}

/** Concatenate the raw text content of a `<code>` element. */
function textContentOf(node: Element): string {
  let out = '';
  visit(node, 'text', (t: Text) => {
    out += t.value;
  });
  return out;
}

/**
 * Post-sanitize rehype step (spec §4/§7): runs AFTER the sanitize boundary and
 * BEFORE Shiki. It rewrites `pre > code.language-{mermaid,vega,vega-lite}` into
 * a `<div class="mp-embed" data-embed="…">…source…</div>`, replacing the whole
 * `<pre>`. This (a) makes Shiki skip the block (it's no longer a code element)
 * and (b) lets `rehype-react` map `div.mp-embed` to a lazy client renderer.
 *
 * The source is kept verbatim as the div's single text child — it is treated as
 * untrusted by the renderer (Mermaid SVG is DOMPurified; charts parse JSON).
 */
export function rehypeEmbeds(embeds: Record<string, string> = BUILTIN_EMBEDS): Plugin<[], Root> {
  return () => (tree: Root) => {
    visit(tree, 'element', (node: Element) => {
      if (node.tagName !== 'pre') return;
      const code = node.children.find(
        (child): child is Element => child.type === 'element' && child.tagName === 'code',
      );
      if (!code) return;

      const lang = languageOf(code);
      if (!lang) return;
      const embed = embeds[lang];
      if (!embed) return;

      const source = textContentOf(code).replace(/\n$/, '');

      // Mutate the <pre> in place into the embed <div>.
      node.tagName = 'div';
      node.properties = { className: ['mp-embed'], 'data-embed': embed };
      node.children = [{ type: 'text', value: source }];
    });
  };
}
